import React, { useEffect, useState, useCallback } from 'react';
import { showToast } from '../common/Toaster';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Plus, Trash2, Check, Lock, ArrowRight, Info, X, BookOpen,
  MessageSquare, ChevronDown, Eye, Target, Send
} from 'lucide-react';
import planApi, { PlanRole } from '../../services/planApi';
import { useAuth } from '../../contexts/AuthContext';
import PdiExampleModal from './PdiExampleModal';
import './pdi-builder.scss';

interface Props { relId: number; role: PlanRole; counterpart?: string; onChanged: () => void; }

const CATS: Record<string, { label: string; pct: string; color: string }> = {
  '70': { label: 'Experiencia', pct: '70', color: '#A50D27' },
  '20': { label: 'De otros', pct: '20', color: '#0F4E8A' },
  '10': { label: 'Formal', pct: '10', color: '#1F6F4A' },
};
const CAT_ORDER = ['70', '20', '10'];

const BRECHA: Record<string, { cls: string; label: string }> = {
  debilidad: { cls: 'weakness', label: 'Debilidad' },
  amenaza: { cls: 'threat', label: 'Amenaza' },
  oportunidad: { cls: 'opportunity', label: 'Oportunidad' },
  fortaleza: { cls: 'strength', label: 'Fortaleza' },
};

const currentMonth = () => {
  const start = new Date('2026-01-01');
  const now = new Date();
  const diff = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) + 1;
  return Math.max(1, Math.min(12, diff));
};

const fmtDate = (d?: string) => {
  if (!d) return '';
  const dt = new Date(String(d).replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('es', { month: 'short', year: 'numeric' });
};

const PdiBuilder: React.FC<Props> = ({ relId, role, counterpart, onChanged }) => {
  const cpFirst = (counterpart || 'tu mentee').split(' ')[0];
  const [comps, setComps] = useState<any[]>([]);
  const [stage, setStage] = useState<any>(null);
  const [pending, setPending] = useState(false);
  const [fodaFinalized, setFodaFinalized] = useState(false);
  const [norte, setNorte] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'build' | 'informe'>('build');
  const [showExample, setShowExample] = useState(false);
  const [showMethod, setShowMethod] = useState(false);
  const [openComments, setOpenComments] = useState<Record<number, boolean>>({});
  const [commentDraft, setCommentDraft] = useState<Record<number, string>>({});
  const [brechas, setBrechas] = useState<any[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [confirmComp, setConfirmComp] = useState<any | null>(null);
  const [saved, setSaved] = useState(false);
  const readOnly = role !== 'mentee';
  const { user } = useAuth();
  const isDemoAllowed = ['josemanuel@kuali.ai', 'noelia@kuali.ai'].includes((user?.email || '').toLowerCase());

  const load = useCallback(async () => {
    const [d, f] = await Promise.all([
      planApi.getPdi(relId),
      planApi.getFoda(relId).catch(() => ({ items: [] })),
    ]);
    setComps(d.competencies || []);
    setStage(d.stage || null);
    setPending(!!d.pending);
    setFodaFinalized(!!d.fodaFinalized);
    setNorte(d.norte || '');
    setBrechas((f.items || []).filter((i: any) => i.quadrant !== 'fortaleza'));
  }, [relId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (readOnly) setView('informe'); }, [readOnly]);

  const finalized = stage?.status === 'finalized';

  const saveNorte = async () => { await planApi.savePdiNorte(relId, norte); onChanged(); };
  const saveDraft = async () => { await planApi.savePdiNorte(relId, norte); setSaved(true); await load(); onChanged(); setTimeout(() => setSaved(false), 1800); };
  const openPicker = () => {
    setPicked(new Set<number>(comps.map((c: any) => Number(c.foda_item_id)).filter(Boolean)));
    setShowPicker(true);
  };
  const togglePick = (id: number) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const createFromPicker = async () => {
    const ids = Array.from(picked);
    if (!ids.length) { showToast('Marca al menos una brecha para crear objetivos.', 'error'); return; }
    setBusy(true);
    try { await planApi.seedPdi(relId, ids); setShowPicker(false); await load(); onChanged(); }
    catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo generar', 'error'); }
    finally { setBusy(false); }
  };
  const updComp = async (id: number, field: string, value: string) => { await planApi.updateCompetency(id, { [field]: value }); await load(); onChanged(); };
  const delComp = (c: any) => setConfirmComp(c);
  const doDelComp = async () => {
    const c = confirmComp; setConfirmComp(null);
    if (!c) return;
    await planApi.deleteCompetency(c.id); await load(); onChanged();
  };
  const addAction = async (compId: number) => { await planApi.addAction(compId, { text: '', category: '70' }); await load(); onChanged(); };
  const updAction = async (id: number, data: any) => { await planApi.updateAction(id, data); await load(); onChanged(); };
  const cycleCat = async (a: any) => { const next = CAT_ORDER[(CAT_ORDER.indexOf(String(a.category)) + 1) % 3]; await updAction(a.id, { category: next }); };
  const toggleDone = async (id: number) => { await planApi.completeAction(id); await load(); onChanged(); };
  const delAction = async (id: number) => { await planApi.deleteAction(id); await load(); onChanged(); };
  const finalize = async () => { setBusy(true); try { await planApi.finalizePdi(relId); await load(); onChanged(); } catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo finalizar', 'error'); } finally { setBusy(false); } };
  const sendComment = async (compId: number) => {
    const t = (commentDraft[compId] || '').trim(); if (!t) return;
    await planApi.addPdiComment(compId, t);
    setCommentDraft(d => ({ ...d, [compId]: '' }));
    await load(); onChanged();
  };
  const loadDemo = async () => {
    setBusy(true);
    try {
      await planApi.seedPdi(relId);
      const d = await planApi.getPdi(relId);
      for (const c of (d.competencies || [])) {
        if ((c.actions || []).length === 0) {
          await planApi.addAction(c.id, { text: 'Liderar un proyecto real que ejercite esta competencia', category: '70' });
          await planApi.addAction(c.id, { text: 'Sesiones de mentoría / shadowing con un referente', category: '20' });
          await planApi.addAction(c.id, { text: 'Curso o lectura estructurada sobre el tema', category: '10' });
        }
      }
      const demoNorte = 'Consolidar mi posicionamiento y cerrar las brechas prioritarias de mi FODA al final del programa.';
      if (!norte) { setNorte(demoNorte); await planApi.savePdiNorte(relId, demoNorte); }
      await load(); onChanged();
    } catch (e: any) { showToast(e?.response?.data?.message || 'Primero finaliza el FODA', 'error'); }
    finally { setBusy(false); }
  };

  if (pending) {
    return (
      <div className="plan-card">
        <div className="plan-locked">
          <Lock size={28} className="lock-ic" />
          <h3>PDI en preparación</h3>
          <p>Tu mentee aún no ha entregado su PDI. Lo verás aquí cuando lo finalice.</p>
        </div>
      </div>
    );
  }

  const totalActions = comps.reduce((n, c) => n + (Number(c.actionsTotal) || 0), 0);
  const doneActions = comps.reduce((n, c) => n + (Number(c.actionsDone) || 0), 0);
  const globalPct = totalActions > 0 ? Math.round((doneActions / totalActions) * 100) : 0;
  const canFinalize = comps.length > 0 && comps.every(c => (c.title || '').trim() && (c.actions || []).length > 0);

  // ================= INFORME =================
  const informe = (
    <div className="pdi-wrap">
      {showExample && <PdiExampleModal onClose={() => setShowExample(false)} />}
      <header className="pdi-head">
        <div>
          {readOnly && <span className="pdi-role-pill mentor">Vista · Mentor</span>}
          <div className="pdi-kicker">Informe del PDI · Mes {currentMonth()} de 12</div>
          <h1 className="pdi-title">{readOnly ? <>PDI de <span>{cpFirst}</span></> : <>Informe de <span>mi PDI</span></>}</h1>
        </div>
        <div className="pdi-head-actions">
          {!readOnly && <button className="pdi-toggle" onClick={() => setView('build')}><Target size={13} /> Volver al constructor</button>}
          <button className="pdi-ex-btn" onClick={() => setShowExample(true)}><Eye size={13} /> Ver ejemplo</button>
        </div>
      </header>

      <div className="pdi-stats">
        <div className="pdi-stat"><div className="pdi-stat-mono">Objetivos</div><div className="pdi-stat-val">{comps.length}</div></div>
        <div className="pdi-stat"><div className="pdi-stat-mono">Acciones</div><div className="pdi-stat-val">{doneActions}/{totalActions}</div></div>
        <div className="pdi-stat"><div className="pdi-stat-mono">Avance</div><div className="pdi-stat-val">{globalPct}%</div></div>
        <div className="pdi-stat"><div className="pdi-stat-mono">Estado</div><div className="pdi-stat-val">{finalized ? 'Entregado' : 'En curso'}</div></div>
      </div>

      {norte && (
        <div className="pdi-informe-norte">
          <div className="pdi-mono-label">Mi norte</div>
          <div className="pdi-informe-norte-text">{norte}</div>
        </div>
      )}

      {comps.map((c, idx) => {
        const pct = Number(c.actionsTotal) > 0 ? Math.round((Number(c.actionsDone) / Number(c.actionsTotal)) * 100) : 0;
        const state = pct >= 100 ? 'logrado' : pct > 0 ? 'parcial' : 'activo';
        return (
          <div className="pdi-informe-obj" key={c.id}>
            <div className="pdi-informe-obj-bar">
              <div>
                <div className="pdi-informe-obj-num">Objetivo {String(idx + 1).padStart(2, '0')}</div>
                <div className="pdi-informe-obj-title">{c.title || 'Sin título'}</div>
              </div>
              <span className={`pdi-informe-obj-state ${state}`}>{state === 'logrado' ? 'Logrado' : state === 'parcial' ? `${pct}%` : 'Activo'}</span>
            </div>
            <div className="pdi-informe-sec">
              <div className="pdi-mono-label">Acciones</div>
              {(c.actions || []).length === 0 ? <p className="pdi-mini-empty">Sin acciones.</p> : (
                (c.actions || []).map((a: any) => (
                  <div className="pdi-informe-action" key={a.id}>
                    <span className="pdi-informe-cat" style={{ background: CATS[a.category]?.color }}>{CATS[a.category]?.pct}</span>
                    <span className={`pdi-informe-atext ${Number(a.completed) === 1 ? 'done' : ''}`}>{a.text || <em>(sin texto)</em>}</span>
                    <span className="pdi-informe-adate">{Number(a.completed) === 1 ? '✓ ' : ''}{fmtDate(a.review_date)}</span>
                  </div>
                ))
              )}
            </div>
            <div className="pdi-informe-sec closing">
              <div className="pdi-mono-label">Notas del mentor</div>
              {(c.comments || []).length === 0
                ? <p className="pdi-mini-empty">Sin comentarios del mentor.</p>
                : (c.comments || []).map((cm: any) => (
                  <div className="pdi-mnote" key={cm.id}>
                    <div className="pdi-mnote-meta">{cm.author_role === 'mentor' ? 'Mentor' : cm.author_role}{cm.author_name ? `: ${cm.author_name}` : ''} · {fmtDate(cm.created_at)}</div>
                    <div className="pdi-mnote-text">{cm.text}</div>
                  </div>
                ))}
              {readOnly && (
                <div className="pdi-comment-add">
                  <input placeholder="Escribe un comentario para tu mentee…" value={commentDraft[c.id] || ''}
                    onChange={e => setCommentDraft(d => ({ ...d, [c.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') sendComment(c.id); }} />
                  <button disabled={!(commentDraft[c.id] || '').trim()} onClick={() => sendComment(c.id)}><Send size={14} /></button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {comps.length === 0 && <p className="pdi-mini-empty">Este PDI aún no tiene objetivos.</p>}
    </div>
  );

  if (readOnly || view === 'informe') return informe;

  // ================= CONSTRUCTOR =================
  return (
    <div className="pdi-wrap">
      <ConfirmDialog
        open={!!confirmComp}
        danger
        title="Quitar este objetivo"
        message={<>Vas a quitar “{confirmComp?.title || 'sin título'}”. Ya no lo trabajarás y se <strong>desmarcará esa brecha de tu FODA</strong>. Sus acciones se eliminarán.</>}
        confirmLabel="Quitar objetivo"
        onConfirm={doDelComp}
        onCancel={() => setConfirmComp(null)}
      />
      {showExample && <PdiExampleModal onClose={() => setShowExample(false)} />}
      {showMethod && (
        <div className="pdi-ex-overlay" onClick={() => setShowMethod(false)}>
          <div className="pdi-method-modal" onClick={e => e.stopPropagation()}>
            <button className="pdi-ex-close" onClick={() => setShowMethod(false)}><X size={18} /></button>
            <div className="pdi-ex-kicker">Metodología</div>
            <h2 className="pdi-ex-title">El modelo <span>70·20·10</span></h2>
            <div className="pdi-method-bar">
              <div className="mb-70"><span className="mb-pct">70%</span><span className="mb-lb">Experiencia</span></div>
              <div className="mb-20"><span className="mb-pct">20%</span><span className="mb-lb">De otros</span></div>
              <div className="mb-10"><span className="mb-pct">10%</span><span className="mb-lb">Formal</span></div>
            </div>
            <div className="pdi-method-cols">
              <div><div className="pmc-h">70% · Experiencia</div><div className="pmc-t">El aprendizaje real viene del rol: proyectos, retos y responsabilidades nuevas en el día a día.</div></div>
              <div><div className="pmc-h">20% · De otros</div><div className="pmc-t">Mentoría, feedback, shadowing y conversaciones con referentes que aceleran tu desarrollo.</div></div>
              <div><div className="pmc-h">10% · Formal</div><div className="pmc-t">Cursos, lecturas y certificaciones. Importante, pero el menor peso del aprendizaje efectivo.</div></div>
            </div>
          </div>
        </div>
      )}

      <header className="pdi-head">
        <div>
          <div className="pdi-kicker">Constructor del PDI · Mes {currentMonth()} de 12</div>
          <h1 className="pdi-title">Construir <span>mi PDI</span></h1>
        </div>
        <div className="pdi-head-actions">
          <button className="pdi-toggle" disabled={busy} onClick={saveDraft}><Check size={13} /> {saved ? 'Guardado' : 'Guardar borrador'}</button>
          <button className="pdi-ex-btn" onClick={() => setShowExample(true)}><Eye size={13} /> Ver ejemplo de PDI <ArrowRight size={12} /></button>
          {isDemoAllowed && <button className="pdi-demo-link" disabled={busy} onClick={loadDemo}>· cargar contenido demo</button>}
        </div>
      </header>

      <div className="pdi-method">
        <div className="pdi-method-icon"><BookOpen size={18} /></div>
        <div>
          <div className="pdi-method-title">Metodología 70·20·10
            <button className="pdi-method-info" onClick={() => setShowMethod(true)} title="Saber más"><Info size={13} /></button>
          </div>
          <div className="pdi-method-text">
            Cada acción se etiqueta como <strong>70% experiencia</strong>, <strong>20% aprender de otros</strong> o <strong>10% formal</strong>.
            El sistema calcula tu distribución y te avisa si se desbalancea. Trabaja idealmente 3 competencias en el programa.
          </div>
        </div>
      </div>

      <section className="pdi-section">
        <div className="pdi-sec-kick"><span className="pdi-sec-num">01 —</span><span className="pdi-sec-lbl">Tu norte</span></div>
        <h2 className="pdi-sec-title">¿Dónde quieres estar al <span>final del programa</span>?</h2>
        <p className="pdi-sec-meta">Tu visión de éxito al cierre. Una o dos frases que capturen lo que quieres haber logrado.</p>
        <textarea className="pdi-norte" value={norte} onChange={e => setNorte(e.target.value)} onBlur={saveNorte}
          placeholder="Ej: Haber consolidado mi posicionamiento como referente financiero de la transición EV en LatAm, con narrativa estratégica frente al board global…" />
      </section>

      <section className="pdi-section">
        <div className="pdi-sec-kick"><span className="pdi-sec-num">02 —</span><span className="pdi-sec-lbl">Objetivos desde tus brechas</span></div>
        <h2 className="pdi-sec-title">Un objetivo por cada <span>brecha prioritaria</span></h2>
        <p className="pdi-sec-meta">Nacen de las líneas que priorizaste en el FODA. Cada objetivo se trabaja con acciones bajo el modelo 70/20/10.</p>

        {comps.length === 0 ? (
          <div className="pdi-empty">
            <Target size={30} className="pdi-empty-ic" />
            <div className="pdi-empty-title">Elige tus objetivos</div>
            <p className="pdi-empty-text">{fodaFinalized ? 'Selecciona qué brechas de tu FODA vas a trabajar. Cada una se convierte en un objetivo.' : 'Primero finaliza tu FODA para elegir los objetivos.'}</p>
            <button className="pdi-add-first" disabled={busy || !fodaFinalized} onClick={openPicker}><Plus size={16} /> Elegir brechas a trabajar</button>
          </div>
        ) : comps.map((c, idx) => {
          const b = c.balance || { p70: 0, p20: 0, p10: 0, tone: 'empty', text: 'Aún no hay acciones.' };
          const pct = Number(c.actionsTotal) > 0 ? Math.round((Number(c.actionsDone) / Number(c.actionsTotal)) * 100) : 0;
          const brechaMeta = c.brecha ? BRECHA[c.brecha.quadrant] : null;
          return (
            <div className="pdi-obj" key={c.id}>
              <div className="pdi-obj-bar">
                {brechaMeta && (
                  <div className="pdi-obj-brecha">
                    <span className={`pdi-obj-brecha-chip ${brechaMeta.cls}`}><span className="dot" /> {brechaMeta.label}</span>
                    <span className="pdi-obj-brecha-txt">{c.brecha.content}</span>
                  </div>
                )}
                <div className="pdi-obj-bar-row">
                  <span className="pdi-obj-num">{String(idx + 1).padStart(2, '0')}</span>
                  <div className="pdi-obj-inputs">
                    <input className="pdi-obj-title-in" defaultValue={c.title} placeholder="Título del objetivo…"
                      onBlur={e => { if (e.target.value !== c.title) updComp(c.id, 'title', e.target.value); }} />
                    <textarea className="pdi-obj-why-in" defaultValue={c.description || ''} placeholder="¿Por qué este objetivo? (opcional)"
                      onBlur={e => { if (e.target.value !== (c.description || '')) updComp(c.id, 'description', e.target.value); }} />
                  </div>
                  <button className="pdi-obj-del" onClick={() => delComp(c)} title="Quitar objetivo"><Trash2 size={16} /></button>
                </div>
              </div>

              <div className="pdi-actions">
                {(c.actions || []).map((a: any) => {
                  const cat = CATS[a.category] || CATS['70'];
                  const done = Number(a.completed) === 1;
                  return (
                    <div className={`pdi-action ${done ? 'done' : ''}`} key={a.id}>
                      <button className={`pdi-check ${done ? 'on' : ''}`} onClick={() => toggleDone(a.id)}>{done && <Check size={11} strokeWidth={3} />}</button>
                      <button className={`pdi-cat cat-${a.category}`} onClick={() => cycleCat(a)} title="Cambiar categoría">{cat.label} · {cat.pct}</button>
                      <input className="pdi-action-in" defaultValue={a.text} placeholder="Describe la acción…"
                        onBlur={e => { if (e.target.value !== a.text) updAction(a.id, { text: e.target.value }); }} />
                      <input type="date" className="pdi-action-date" defaultValue={a.review_date || ''} onChange={e => updAction(a.id, { review_date: e.target.value })} />
                      <button className="pdi-action-del" onClick={() => delAction(a.id)} title="Eliminar"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
                <button className="pdi-add-action" onClick={() => addAction(c.id)}><Plus size={14} /> Agregar acción</button>

                <div className="pdi-dist">
                  <div className="pdi-dist-head">
                    <span className="pdi-mono-label">Distribución 70·20·10</span>
                    <span className="pdi-dist-avance">Avance {pct}%</span>
                  </div>
                  <div className="pdi-dist-bars">
                    <span style={{ width: `${b.p70}%`, background: CATS['70'].color }} />
                    <span style={{ width: `${b.p20}%`, background: CATS['20'].color }} />
                    <span style={{ width: `${b.p10}%`, background: CATS['10'].color }} />
                  </div>
                  <div className="pdi-dist-legend">
                    <span><i style={{ background: CATS['70'].color }} /> EXP {b.p70}%</span>
                    <span><i style={{ background: CATS['20'].color }} /> OTROS {b.p20}%</span>
                    <span><i style={{ background: CATS['10'].color }} /> FORMAL {b.p10}%</span>
                  </div>
                  <div className={`pdi-dist-insight ${b.tone === 'ok' ? 'good' : b.tone === 'warn' ? 'warn' : ''}`}>{b.text}</div>
                </div>
              </div>

              <div className="pdi-comments">
                <button className="pdi-comments-btn" onClick={() => setOpenComments(o => ({ ...o, [c.id]: !o[c.id] }))}>
                  <MessageSquare size={13} /> Comentarios del mentor {(c.comments || []).length > 0 && `(${c.comments.length})`}
                  <ChevronDown size={14} className={`pdi-comments-arrow ${openComments[c.id] ? 'up' : ''}`} />
                </button>
                {openComments[c.id] && (
                  <div className="pdi-comments-list">
                    {(c.comments || []).length === 0
                      ? <p className="pdi-mini-empty">Tu mentor aún no ha comentado este objetivo.</p>
                      : (c.comments || []).map((cm: any) => (
                        <div className="pdi-mnote" key={cm.id}>
                          <div className="pdi-mnote-meta">Mentor{cm.author_name ? `: ${cm.author_name}` : ''} · {fmtDate(cm.created_at)}</div>
                          <div className="pdi-mnote-text">{cm.text}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {comps.length > 0 && fodaFinalized && (
          <button className="pdi-add-obj" disabled={busy} onClick={openPicker}><Plus size={16} /> Elegir / agregar brechas a trabajar</button>
        )}
      </section>

      {showPicker && (
        <div className="pdi-picker-overlay" onClick={() => setShowPicker(false)}>
          <div className="pdi-picker" onClick={e => e.stopPropagation()}>
            <h3 className="pdi-picker-title">Elige las brechas a trabajar</h3>
            <p className="pdi-picker-sub">Marca las líneas de tu FODA que quieres convertir en objetivos del PDI. No tienes que trabajar todas.</p>
            <div className="pdi-picker-list">
              {brechas.length === 0 ? (
                <p className="pdi-picker-empty">No hay brechas en tu FODA. Agrega debilidades, oportunidades o amenazas en el paso FODA.</p>
              ) : brechas.map(b => {
                const isObj = comps.some((c: any) => Number(c.foda_item_id) === Number(b.id));
                const on = picked.has(b.id);
                const ql = BRECHA[b.quadrant] || { label: b.quadrant, cls: '' };
                return (
                  <label key={b.id} className={`pdi-picker-item ${on ? 'on' : ''} ${isObj ? 'is-obj' : ''}`}>
                    <input type="checkbox" checked={on} disabled={isObj} onChange={() => togglePick(b.id)} />
                    <span className={`pdi-picker-q ${ql.cls}`}>{ql.label}</span>
                    <span className="pdi-picker-txt">{b.content}</span>
                    {isObj && <span className="pdi-picker-tag">Ya es objetivo</span>}
                  </label>
                );
              })}
            </div>
            <div className="pdi-picker-actions">
              <button className="pdi-footer-btn primary" disabled={busy} onClick={createFromPicker}><Check size={15} /> Crear objetivos</button>
              <button className="pdi-picker-cancel" onClick={() => setShowPicker(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      <div className="pdi-footer">
        <button className="pdi-footer-btn ghost" disabled={busy} onClick={saveDraft}>
          <Check size={15} /> {saved ? 'Guardado' : 'Guardar borrador'}
        </button>
        <button className="pdi-footer-btn primary" disabled={!canFinalize || busy || finalized} onClick={finalize}>
          <Check size={15} /> {finalized ? 'PDI entregado' : 'Finalizar PDI y enviar al mentor'}
        </button>
        <span className="pdi-footer-note">Cada objetivo necesita un título y al menos una acción.</span>
      </div>
    </div>
  );
};

export default PdiBuilder;
